import { SearchX } from "lucide-react";
import { Bar, BarChart, CartesianGrid, XAxis, YAxis } from "recharts";

import { Card, CardContent } from "@/components/ui/v2/card";
import {
  type ChartConfig,
  ChartContainer,
  ChartTooltip,
  ChartTooltipContent,
} from "@/components/ui/v2/chart";
import EmptyState from "@/components/ui/v2/empty-state";

export type HorizontalBarChartItem = {
  label: string;
  value: number;
};

type HorizontalBarChartProps = {
  data: HorizontalBarChartItem[];
  title?: string;
  subtitle?: string;
  tooltipLabel?: string;
  color?: string;
  isLoading?: boolean;
  emptyMessage?: string;
  /** Value-axis scale. Defaults to "linear". Use "log" when values span multiple orders of magnitude. */
  scale?: "linear" | "log";
};

const BAR_HEIGHT = 36;
const MIN_CHART_HEIGHT = 120;

export function HorizontalBarChart({
  data,
  title,
  subtitle,
  tooltipLabel = "Count",
  color = "var(--color-primary)",
  isLoading = false,
  emptyMessage = "No data available",
  scale = "linear",
}: HorizontalBarChartProps) {
  const chartConfig = {
    value: { label: tooltipLabel, color },
  } satisfies ChartConfig;

  const chartHeight = Math.max(data.length * BAR_HEIGHT + 40, MIN_CHART_HEIGHT);

  // Log-scale ticks: decade labels only (1, 10, 100, ...) up to one decade above the largest value,
  // giving the tallest bar visible headroom before the next tick. Recharts' default log ticks label
  // every minor tick (2..9) which is noisy.
  const isLog = scale === "log";
  const maxValue = data.reduce((m, d) => (d.value > m ? d.value : m), 1);
  const logTicks = isLog
    ? Array.from({ length: Math.floor(Math.log10(maxValue)) + 2 }, (_, i) => 10 ** i)
    : undefined;
  const logDomainMax = logTicks ? logTicks[logTicks.length - 1] : undefined;

  return (
    <Card>
      <CardContent className="px-3 py-0 lg:px-4">
        {(title || subtitle) && (
          <div className="mb-3">
            {title && <p className="text-foreground font-semibold text-base">{title}</p>}
            {subtitle && <p className="text-xs text-muted-foreground">{subtitle}</p>}
          </div>
        )}
        {isLoading ? (
          <div
            className="animate-pulse rounded-lg bg-muted"
            style={{ height: MIN_CHART_HEIGHT }}
          />
        ) : data.length === 0 ? (
          <EmptyState icon={SearchX} title={emptyMessage} />
        ) : (
          <ChartContainer config={chartConfig} style={{ height: chartHeight, width: "100%" }}>
            <BarChart data={data} layout="vertical" margin={{ left: 0, right: 16, top: 0, bottom: 4 }}>
              <CartesianGrid
                strokeDasharray="3 3"
                stroke="var(--color-border)"
                horizontalCoordinatesGenerator={({ height, offset }) => {
                  // Draw horizontal grid lines at each band boundary — i.e. between bars, plus the
                  // top and bottom edges of the plot — instead of the default behaviour of drawing
                  // them through each bar's centre. Computed directly from plot geometry to avoid
                  // depending on recharts' internal scale shape.
                  const count = data.length;
                  if (count < 1) return [];
                  const top = offset?.top ?? 0;
                  const bottom = offset?.bottom ?? 0;
                  const plotHeight = height - top - bottom;
                  const band = plotHeight / count;
                  return Array.from({ length: count + 1 }, (_, i) => top + i * band);
                }}
              />
              <YAxis
                dataKey="label"
                type="category"
                tickLine={false}
                axisLine={false}
                width={120}
                tick={{ fontSize: 12, fill: "var(--color-foreground)", textAnchor: "end" }}
              />
              <XAxis
                type="number"
                orientation="top"
                tickLine={false}
                axisLine={false}
                tick={{ fontSize: 12, fill: "var(--color-muted-foreground)" }}
                allowDecimals={false}
                scale={isLog ? "log" : "linear"}
                domain={isLog ? [1, logDomainMax ?? "dataMax"] : [0, "dataMax"]}
                ticks={logTicks}
              />
              <ChartTooltip
                cursor={false}
                content={<ChartTooltipContent hideLabel />}
                isAnimationActive={false}
              />
              <Bar dataKey="value" fill={color} radius={[0, 2, 2, 0]} />
            </BarChart>
          </ChartContainer>
        )}
      </CardContent>
    </Card>
  );
}
